import React from 'react';
import { axios, API_URL, useState, toast, useRef } from '../../../Data/Imports';

const masterTypes = {
	Category: 1,
	SubCategory: 2,
	Size: 3,
	Color: 4,
	Brand: 5,
	StyleCode: 6,
};

const ProductFields = ({
	productData,
	setProductData,
	thumbnailRef,
	imagesRef,
	setProductImages,
	editMode,
}) => {
	const debounceRef = useRef(null);
	const [category, setCategory] = useState([]);
	const [subCategory, setSubCategory] = useState([]);
	const [size, setSize] = useState([]);
	const [color, setColor] = useState([]);
	const [brand, setBrand] = useState([]);
	const [styleCode, setStyleCode] = useState([]);
	const [showDropdown, setShowDropdown] = useState({
		category: false,
		subCategory: false,
		size: false,
		color: false,
		brand: false,
		styleCode: false,
	});

	// Handle field changes
	const handleChange = (e) => {
		const { name, value } = e.target;

		if (name === 'CategoryIdName')
			fetchDropdownsDebounced(masterTypes.Category, value, setCategory);
		if (name === 'SubCategoryIdName')
			fetchDropdownsDebounced(masterTypes.SubCategory, value, setSubCategory);
		if (name === 'SizeIdName') fetchDropdownsDebounced(masterTypes.Size, value, setSize);
		if (name === 'ColorIdName') fetchDropdownsDebounced(masterTypes.Color, value, setColor);
		if (name === 'BrandIdName') fetchDropdownsDebounced(masterTypes.Brand, value, setBrand);
		if (name === 'StyleCodeIdName')
			fetchDropdownsDebounced(masterTypes.StyleCode, value, setStyleCode);

		setProductData((prevData) => {
			const newData = { ...prevData, [name]: value };

			// Ensure Discount Price doesn't exceed MRP
			if (name === 'DiscountPrice' || name === 'MRP') {
				let mrp = parseFloat(newData.MRP) || 0;
				let discountPrice = parseFloat(newData.DiscountPrice) || 0;

				// Ensure MRP and Discount Price are not less than or equal to 0
				if (mrp <= 0) {
					mrp = 1; // Set MRP to 1 if it is 0 or less
					newData.MRP = mrp;
				}

				if (discountPrice < 0) {
					discountPrice = 0; // Set Discount Price to 1 if it is 0 or less
					newData.DiscountPrice = discountPrice;
				}

				// If discount price is greater than MRP, set it equal to MRP
				if (discountPrice > mrp) {
					newData.DiscountPrice = mrp;
				}

				// Calculate net price and ensure it doesn't go below 0
				const netPrice = Math.max(0, mrp - discountPrice);
				newData.NetPrice = netPrice;
			}

			// Ensure Stock Quantity can't be 0 or negative
			if (name === 'StockQty') {
				const stockQty = parseInt(newData.StockQty, 10);
				if (stockQty <= 0) {
					newData.StockQty = 1; // Set the stock quantity to 1 if it's 0 or less
				}
			}

			return newData;
		});
	};

	// Handle image change
	const handleImageChange = (e) => {
		const file = e.target.files[0];
		setProductData({ ...productData, ImageFile: file, ParentImagePath: URL.createObjectURL(file) });
	};

	// Handle Multiple image addition
	const handleAddMultipleImages = (e) => {
		const files = Array.from(e.target.files);

		if (files.length > 4) {
			toast.warn('You can only select up to 4 images.');
			e.target.value = ''; // Reset file input
			return;
		}

		const imageArray = files.map((file) => ({
			ImageFile: file,
			ImageName: file.name,
			ImagePath: URL.createObjectURL(file), // Preview URL
		}));

		setProductImages(imageArray);
	};

	const fetchDropdownsDebounced = (typeId, value, setter) => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			fetchDropdowns(typeId, value, setter);
		}, 300);
	};

	const fetchDropdowns = async (typeId, value, setter) => {
		const masterData = {
			MasterTypeId: typeId,
			MasterValue: value,
		};
		try {
			const response = await axios.post(`${API_URL}/master/get/master-entry-dropdown`, masterData);
			setter(response?.data?.dropdownData || []);
		} catch {
			setter([]);
		}
	};

	const handleSelect = (field, selectedId, selectedValue) => {
		setProductData((prevData) => ({
			...prevData,
			[field]: selectedId,
			[`${field}Name`]: selectedValue,
		}));
		setShowDropdown((prev) => ({ ...prev, [field.toLowerCase()]: false }));
	};

	// Fucniton to create new Master Entry
	const handleAddNewEntry = async (
		fieldName,
		MasterTypeId,
		stateSetter,
		handleSelect,
		MasterValue,
		name
	) => {
		try {
			if (!MasterValue?.trim()) {
				toast.error(`Please enter a valid ${name}`);
				return;
			}

			const response = await axios.post(`${API_URL}/master/post/add-master-entry`, {
				MasterTypeId,
				MasterValue,
			});

			const { data } = response;

			if (data?.success) {
				toast.success(`${name} Added Successfully`);
				const newEntry = { Id: data.masterId, MasterValue };

				stateSetter((prev) => [...prev, newEntry]); // Add to respective dropdown state
				handleSelect(fieldName, newEntry.Id, newEntry.MasterValue); // Auto-select new entry
			} else {
				toast.error(`Failed to add ${fieldName}`);
			}
		} catch (error) {
			toast.error(`Error adding ${fieldName}:`);
		}
	};

	return (
		<div className='grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6'>
			{/* Product Name */}
			<div>
				<label>Product Name</label>
				<input
					type='text'
					name='ProductName'
					value={productData.ProductName}
					onChange={handleChange}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Product Name'
					required
				/>
			</div>

			{/* Category */}
			<div className='relative'>
				<label>Select Category</label>
				<input
					type='text'
					name='CategoryIdName'
					value={productData?.CategoryIdName}
					onChange={(e) => handleChange(e)}
					onFocus={() => setShowDropdown((prev) => ({ ...prev, category: true }))}
					onBlur={() => setShowDropdown((prev) => ({ ...prev, category: false }))}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Category'
					required
				/>

				{showDropdown.category && (
					<div className='absolute bg-gray-100 border border-gray-600 w-full mt-2 z-10'>
						<ul>
							{category.map((data, index) => (
								<li
									key={index}
									onMouseDown={() => handleSelect('CategoryId', data.Id, data.MasterValue)}
									className='p-2 cursor-pointer hover:bg-gray-200'>
									{data.MasterValue}
								</li>
							))}

							{/* Check if no matching category exists */}
							{!category.some(
								(data) =>
									data.MasterValue.toLowerCase() === productData?.CategoryIdName?.toLowerCase()
							) &&
								productData?.CategoryIdName?.trim() && (
									<li>
										<button
											onMouseDown={() =>
												handleAddNewEntry(
													'CategoryId',
													masterTypes.Category,
													setCategory,
													handleSelect,
													productData?.CategoryIdName,
													'Category'
												)
											}
											className='p-2 w-full text-left globalButton text-white'>
											+ Add New Category
										</button>
									</li>
								)}
						</ul>
					</div>
				)}
			</div>

			{/* SubCategory */}
			<div className='relative'>
				<label>Select SubCategory</label>
				<input
					type='text'
					name='SubCategoryIdName'
					value={productData?.SubCategoryIdName}
					onChange={(e) => handleChange(e)}
					onFocus={() => setShowDropdown((prev) => ({ ...prev, subCategory: true }))}
					onBlur={() => setShowDropdown((prev) => ({ ...prev, subCategory: false }))}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter SubCategory'
					required
				/>

				{showDropdown.subCategory && (
					<div className='absolute bg-gray-100 border border-gray-600 w-full mt-2 z-10'>
						<ul>
							{subCategory.map((data, index) => (
								<li
									key={index}
									onMouseDown={() => handleSelect('SubCategoryId', data.Id, data.MasterValue)}
									className='p-2 cursor-pointer hover:bg-gray-200'>
									{data.MasterValue}
								</li>
							))}

							{/* Show "Add New SubCategory" button only when no match is found */}
							{!subCategory.some(
								(data) =>
									data.MasterValue.toLowerCase() === productData?.SubCategoryIdName?.toLowerCase()
							) &&
								productData?.SubCategoryIdName?.trim() && (
									<li>
										<button
											onMouseDown={() =>
												handleAddNewEntry(
													'SubCategoryId',
													masterTypes.SubCategory,
													setSubCategory,
													handleSelect,
													productData?.SubCategoryIdName,
													'SubCategory'
												)
											}
											className='p-2 w-full text-left globalButton text-white'>
											+ Add New SubCategory
										</button>
									</li>
								)}
						</ul>
					</div>
				)}
			</div>

			{/* Size */}
			<div className='relative'>
				<label>Select Size</label>
				<input
					type='text'
					name='SizeIdName'
					value={productData?.SizeIdName}
					onChange={(e) => handleChange(e)}
					onFocus={() => setShowDropdown((prev) => ({ ...prev, size: true }))}
					onBlur={() => setShowDropdown((prev) => ({ ...prev, size: false }))}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Size'
					required
				/>

				{showDropdown.size && (
					<div className='absolute bg-gray-100 border border-gray-600 w-full mt-2 z-10'>
						<ul>
							{size?.map((data, index) => (
								<li
									key={index}
									onMouseDown={() => handleSelect('SizeId', data.Id, data.MasterValue)}
									className='p-2 cursor-pointer hover:bg-gray-200'>
									{data.MasterValue}
								</li>
							))}

							{/* Show "Add New Size" button only when no match is found */}
							{!size.some(
								(data) => data.MasterValue.toLowerCase() === productData?.SizeIdName?.toLowerCase()
							) &&
								productData?.SizeIdName?.trim() && (
									<li>
										<button
											onMouseDown={() =>
												handleAddNewEntry(
													'SizeId',
													masterTypes.Size,
													setSize,
													handleSelect,
													productData?.SizeIdName,
													'Size'
												)
											}
											className='p-2 w-full text-left globalButton text-white'>
											+ Add New Size
										</button>
									</li>
								)}
						</ul>
					</div>
				)}
			</div>

			{/* Color */}
			<div className='relative'>
				<label>Select Color</label>
				<input
					type='text'
					name='ColorIdName'
					value={productData?.ColorIdName}
					onChange={(e) => handleChange(e)}
					onFocus={() => setShowDropdown((prev) => ({ ...prev, color: true }))}
					onBlur={() => setShowDropdown((prev) => ({ ...prev, color: false }))}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Color'
					required
				/>

				{showDropdown.color && (
					<div className='absolute bg-gray-100 border border-gray-600 w-full mt-2 z-10'>
						<ul>
							{color?.map((data, index) => (
								<li
									key={index}
									onMouseDown={() => handleSelect('ColorId', data.Id, data.MasterValue)}
									className='p-2 cursor-pointer hover:bg-gray-200'>
									{data.MasterValue}
								</li>
							))}

							{/* Show "Add New Color" button only when no match is found */}
							{!color.some(
								(data) => data.MasterValue.toLowerCase() === productData?.ColorIdName?.toLowerCase()
							) &&
								productData?.ColorIdName?.trim() && (
									<li>
										<button
											onMouseDown={() =>
												handleAddNewEntry(
													'ColorId',
													masterTypes.Color,
													setColor,
													handleSelect,
													productData?.ColorIdName,
													'Color'
												)
											}
											className='p-2 w-full text-left globalButton text-white'>
											+ Add New Color
										</button>
									</li>
								)}
						</ul>
					</div>
				)}
			</div>

			{/* Brand */}
			<div className='relative'>
				<label>Select Brand</label>
				<input
					type='text'
					name='BrandIdName'
					value={productData?.BrandIdName}
					onChange={(e) => handleChange(e)}
					onFocus={() => setShowDropdown((prev) => ({ ...prev, brand: true }))}
					onBlur={() => setShowDropdown((prev) => ({ ...prev, brand: false }))}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Brand'
					required
				/>

				{showDropdown.brand && (
					<div className='absolute bg-gray-100 border border-gray-600 w-full mt-2 z-10'>
						<ul>
							{brand?.map((data, index) => (
								<li
									key={index}
									onMouseDown={() => handleSelect('BrandId', data.Id, data.MasterValue)}
									className='p-2 cursor-pointer hover:bg-gray-200'>
									{data.MasterValue}
								</li>
							))}

							{/* Show "Add New Brand" button only when no match is found */}
							{!brand.some(
								(data) => data.MasterValue.toLowerCase() === productData?.BrandIdName?.toLowerCase()
							) &&
								productData?.BrandIdName?.trim() && (
									<li>
										<button
											onMouseDown={() =>
												handleAddNewEntry(
													'BrandId',
													masterTypes.Brand,
													setBrand,
													handleSelect,
													productData?.BrandIdName,
													'Brand'
												)
											}
											className='p-2 w-full text-left globalButton text-white'>
											+ Add New Brand
										</button>
									</li>
								)}
						</ul>
					</div>
				)}
			</div>

			{/* StyleCode */}
			<div className='relative'>
				<label> Style </label>
				<input
					type='text'
					name='StyleCodeIdName'
					value={productData?.StyleCodeIdName}
					onChange={(e) => handleChange(e)}
					onFocus={() => setShowDropdown((prev) => ({ ...prev, styleCode: true }))}
					onBlur={() => setShowDropdown((prev) => ({ ...prev, styleCode: false }))}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Style'
					required
				/>

				{showDropdown.styleCode && (
					<div className='absolute border bg-gray-100 border-gray-600 w-full mt-2 z-10'>
						<ul>
							{styleCode?.map((data, index) => (
								<li
									key={index}
									onMouseDown={() => handleSelect('StyleCodeId', data.Id, data.MasterValue)}
									className='p-2 cursor-pointer hover:bg-gray-200'>
									{data.MasterValue}
								</li>
							))}

							{/* Show "Add New Style" button only when no match is found */}
							{!styleCode.some(
								(data) =>
									data.MasterValue.toLowerCase() === productData?.StyleCodeIdName?.toLowerCase()
							) &&
								productData?.StyleCodeIdName?.trim() && (
									<li>
										<button
											onMouseDown={() =>
												handleAddNewEntry(
													'StyleCodeId',
													masterTypes.StyleCode,
													setStyleCode,
													handleSelect,
													productData?.StyleCodeIdName,
													'Style Code'
												)
											}
											className='p-2 w-full text-left globalButton text-white'>
											+ Add New Style
										</button>
									</li>
								)}
						</ul>
					</div>
				)}
			</div>

			<div>
				<label>Enter Stock Quantity</label>
				<input
					type='number'
					name='StockQty'
					value={productData.StockQty}
					onChange={handleChange}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Stock Quantity'
					required
				/>
			</div>
			<div>
				<label>Enter MRP</label>
				<input
					type='number'
					name='MRP'
					value={productData.MRP}
					onChange={handleChange}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter MRP'
					required
				/>
			</div>
			<div>
				<label>Enter Discount Price</label>
				<input
					type='number'
					name='DiscountPrice'
					value={productData.DiscountPrice}
					onChange={handleChange}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Discount Price'
					required
				/>
			</div>
			<div>
				<label>Enter Net Price</label>
				<input
					type='number'
					name='NetPrice'
					value={productData.NetPrice}
					onChange={handleChange}
					className='border p-2 rounded w-full mt-2'
					placeholder='Net Price'
					required
					disabled
				/>
			</div>
			<div className=' md:col-span-3 sm:col-span-2 col-span-1'>
				<label>Enter Description</label>
				<input
					type='textarea'
					name='Description'
					value={productData.Description}
					maxLength={200}
					onChange={handleChange}
					className='border p-2 rounded w-full mt-2'
					placeholder='Enter Description'
					required
				/>
			</div>
			{!editMode && (
				<div className=' md:col-span-3 sm:col-span-2 col-span-1 grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1'>
					<div className=' col-span-1'>
						<label>Product Thumbnail</label>
						<input
							type='file'
							name='ParentImagePath'
							required
							ref={thumbnailRef}
							onChange={handleImageChange}
							className='border p-2 rounded w-full mt-2'
						/>
					</div>
					<div className=' col-span-1'>
						<label>Product Images</label>
						<input
							type='file'
							name='productImages'
							multiple
							required
							ref={imagesRef}
							onChange={handleAddMultipleImages}
							className='border p-2 rounded w-full mt-2'
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default ProductFields;
